import React, { useMemo } from "react";
import { ConnectedProps, connect } from "react-redux";
import { Quill } from "react-quill";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";

import { RootState } from "us.helper/types";
import { $TextEditor, $Form, $Button, $MessageBox } from "us.common/components";
import * as Actions from "actions";
import { Parameters } from "components";
import "./SMSEditor.scss";
import {
  generateSMSContent,
  pasteHtmlAtCaret,
  editorContentToSmsTemplate,
  smsTemplateToEditorContent,
} from "components/TemplateEditor/Functions";
import {
  getSectionByName,
  getSectionRuleByRule,
  updateTemplateSectionRule,
} from "functions/Functions";
import { ISectionRule } from "interfaces/Templates";
import { purifySMSContent } from "functions/TemplateEditor";

type SMSEditorProps = {
  onClose: () => void;
};

const Block = Quill.import("blots/block");
Block.tagName = "P"; // keep block-level elements so line breaks render correctly
Quill.register(Block, true);

let Inline = Quill.import("blots/inline");

class ParamsBlot extends Inline {
  static create() {
    let node = super.create();
    node.setAttribute("contenteditable", "false");
    return node;
  }

  static value(node: { getAttribute: (arg0: string) => any }) {
    return {
      contenteditable: node.getAttribute("contenteditable"),
    };
  }
}
ParamsBlot.blotName = "token";
ParamsBlot.tagName = "params";

Quill.register(ParamsBlot);

const SMSEditor: React.FC<SMSEditorProps & PropsFromRedux> = ({
  section,
  drawerInfo,
  template,
  updateTemplate,
  onClose,
}) => {
  const { t } = useTranslation();

  const initialEditorContent = useMemo(
    () => smsTemplateToEditorContent(drawerInfo?.content),
    [drawerInfo?.content]
  );

  const handleParamsBinding = (
    tokenName: string,
    isRepeating: boolean,
    adapterId?: number
  ) => {
    try {
      if (window.getSelection) {
        let windowSelection: any = window.getSelection();
        const qlEditor = document.querySelector(".ql-editor");
        if (qlEditor && qlEditor?.contains(windowSelection?.anchorNode)) {
          pasteHtmlAtCaret(tokenName);
        } else {
          $MessageBox(
            "error",
            `US.USC.TEMPLATEEDITOR:MESSAGES.PLEASE_SELECT_REPLACEABLE_TEXT_FROM_THE_EDITOR`,
            "",
            ""
          );
        }
      }
    } catch (error) {
      $MessageBox(
        "error",
        `US.USC.TEMPLATEEDITOR:MESSAGES.PARAMETER_BINDING_FAILED`,
        "",
        ""
      );
    }
  };

  /**
   * @description - Handle save the changes to selected section
   */
  const handleSave = (values: any) => {
    try {
      if (values.content) {
        const oldSection = getSectionByName(
          section.name,
          template.data.templateContent.pages
        );
        if (oldSection) {
          const oldSectionRule = getSectionRuleByRule(
            section.selectedRule,
            oldSection
          );
          if (oldSectionRule) {
            const normalizedContent = editorContentToSmsTemplate(
              values.content
            );
            const { content: generatedContent } = generateSMSContent(
              oldSectionRule,
              normalizedContent
            );
            const purifiedContent = purifySMSContent(generatedContent);
            const updatedSectionRule: ISectionRule = {
              ...oldSectionRule,
              content: purifiedContent ?? oldSectionRule.content,
            };
            const updatedTemplate = updateTemplateSectionRule(
              section.name,
              section.selectedRule,
              updatedSectionRule,
              template.data
            );
            updateTemplate && updateTemplate(updatedTemplate);
          }
        }
      }
    } catch (error) {
      console.error("Error saving the SMS Editor", error);
      $MessageBox(
        "error",
        `US.USC.TEMPLATEEDITOR:MESSAGES.TEMPLATE_SECTION_UPDATE_FAILED`,
        "",
        ""
      );
    } finally {
      onClose();
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{ content: initialEditorContent }}
      onSubmit={(values) => {
        handleSave(values);
      }}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValidating,
        resetForm,
        setFieldValue,
        ...rest
      }: any) => (
        <$Form>
          <div className="section-editor-layout">
            <div className="section-editor-layout-content">
              <$TextEditor
                name="content"
                value={values.content}
                handleChange={(content: any) =>
                  setFieldValue("content", content)
                }
                modules={{ toolbar: false }}
                formats={{}}
                placeholder={t(
                  "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.TYPE_YOUR_TEXT_HEAR"
                )}
              />
            </div>
            <div className="section-editor-layout-side">
              <Parameters
                disabledAddRepeatingAdapter={true}
                onDoubleClick={handleParamsBinding}
              />
            </div>
          </div>
          <div className="drawer-footer-fixed align-content-center justify-content-end">
            <div>
              <$Button onClick={handleSubmit} type="primary">
                {t("US.COLLECTION.COMMON:COMMON.SAVE")}
              </$Button>
            </div>
          </div>
        </$Form>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { templateEditor, templatesHistory } = state;
  const { parameters, templateRules, section, drawerInfo } = templateEditor;
  const { template } = templatesHistory;

  return {
    tokens: parameters.standard.dataTokens,
    templateRules,
    template,
    section,
    drawerInfo,
  };
};

const { section } = Actions.templateEditor;
const { template } = Actions.templatesHistory;

const mapDispatchToProps = {
  selectSection: section.selectSection,
  updateTemplate: template.update,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(SMSEditor);
